import {Pressable} from 'react-native'

/**
 * The four pieces the card's stage is assembled from.
 *
 * Split out of `PdfCarousel.web.tsx` (23 Sep 2026) so the card is a file a human can
 * hold: each of these is already a leaf — driven entirely by its props, with no view of
 * the document behind it — which is exactly the shape that belongs in its own file.
 * They are the card's *chrome*: the overlay a piece of stage furniture rises into, the
 * button that raises it, and the control that labels it.
 */

/** A layer of stage furniture, drawn over the page rather than beside it. */
export function StageOverlay({children}: {children: React.ReactNode}) {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        backgroundColor: 'rgba(0, 0, 0, 0.45)',
        pointerEvents: 'none',
      }}>
      {children}
    </div>
  )
}

export function StageButton({
  side,
  label,
  onPress,
  children,
}: {
  side: 'left' | 'right'
  label: string
  onPress: () => void
  children: React.ReactNode
}) {
  return (
    <div
      style={{
        position: 'absolute',
        top: '50%',
        [side]: 8,
        transform: 'translateY(-50%)',
        display: 'flex',
      }}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={label}
        accessibilityHint=""
        onPress={onPress}
        style={({pressed}) => [
          {
            width: 40,
            height: 40,
            borderRadius: 99,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.55)',
            opacity: pressed ? 0.8 : 1,
            cursor: 'pointer',
          },
        ]}>
        {children}
      </Pressable>
    </div>
  )
}

export function StageControl({
  label,
  onPress,
  children,
}: {
  label: string
  onPress: () => void
  children: React.ReactNode
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityHint=""
      onPress={onPress}
      style={({pressed}) => [
        {
          width: 32,
          height: 32,
          borderRadius: 99,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.55)',
          opacity: pressed ? 0.8 : 1,
          cursor: 'pointer',
        },
      ]}>
      {children}
    </Pressable>
  )
}
